/**
 * GameDetail Component
 * Displays full game information with status transition actions
 * Shows all gameData fields individually in organized sections
 */

import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Chip,
  Divider,
  Alert,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableHead
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Refresh as RefreshIcon,
  ContentCopy as CopyIcon,
  Undo as UndoIcon
} from '@mui/icons-material';
import { useGame, useGameByCode, useTransitionStatus, useCancelModifications, useGameFeedback } from '../../hooks/useGames';
import { Loading } from '../Common/Loading';
import { ErrorMessage } from '../Common/ErrorMessage';
import { StatusBadge } from './StatusBadge';
import { ReleaseChannelBadge } from './ReleaseChannelBadge';
import { BetaFeedbackBadge, isReadyForPromotion, hasIssues } from './BetaFeedbackBadge';
import {
  GAME_STATUS,
  SCORING_TYPES,
  SCORING_TYPE_LABELS,
  SCORING_TYPE_DESCRIPTIONS,
  ROUND_BEHAVIOR_LABELS,
  PHASE_END_TYPE_LABELS,
  PHASE_TRIGGER_LABELS,
  RELEASE_CHANNELS
} from '../../utils/constants';
import { format } from 'date-fns';

export const GameDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [transitionError, setTransitionError] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);

  const { data: game, isLoading, error, refetch } = useGame(id);
  const transitionMutation = useTransitionStatus();
  const cancelModificationsMutation = useCancelModifications();

  // Fetch beta feedback only for beta channel games
  const isBetaChannel = game?.releaseChannel === RELEASE_CHANNELS.BETA;
  const { data: feedbackData } = useGameFeedback(id, { enabled: isBetaChannel });

  // Fetch parent game for expansions to check promotion eligibility
  const isExpansion = game?.isExpansion && game?.parentGameCode;
  const { data: parentGame } = useGameByCode(
    game?.parentGameCode,
    game?.language,
    { enabled: !!isExpansion }
  );

  // Check if expansion can be promoted to production (parent must be in_production or ready_for_prod)
  // For submitting to review, parent can also be in pending_review (same batch)
  const parentPromotionStatus = useMemo(() => {
    if (!isExpansion) return { canPromote: true, canSubmitForReview: true, reason: null };
    if (!parentGame) return { canPromote: false, canSubmitForReview: false, reason: 'Parent game not found in staging library' };

    const validForPromotion = [GAME_STATUS.IN_PRODUCTION, GAME_STATUS.READY_FOR_PROD];
    const validForReview = [...validForPromotion, GAME_STATUS.PENDING_REVIEW];

    const canPromote = validForPromotion.includes(parentGame.status);
    const canSubmitForReview = validForReview.includes(parentGame.status);

    if (canPromote) {
      return { canPromote: true, canSubmitForReview: true, reason: null };
    }

    if (canSubmitForReview) {
      return {
        canPromote: false,
        canSubmitForReview: true,
        reason: `Parent game "${parentGame.gameName}" must be approved before this expansion can be promoted`
      };
    }

    return {
      canPromote: false,
      canSubmitForReview: false,
      reason: `Parent game "${parentGame.gameName}" (${parentGame.gameCode}) must be submitted for review before this expansion. Current status: ${parentGame.status}`
    };
  }, [isExpansion, parentGame]);

  // Parse gameData and separate known vs unknown fields
  const { gameData, unknownFields } = useMemo(() => {
    if (!game?.gameData) return { gameData: {}, unknownFields: {} };

    const parsed = typeof game.gameData === 'string'
      ? JSON.parse(game.gameData)
      : game.gameData;

    // Known fields that we'll display individually
    const knownFieldNames = [
      'version', 'setupText',
      'minPlayers', 'maxPlayers', 'maxTeams', 'fungibleTeams', 'predefinedTeams', 'teamPoolPoints',
      'scoringType', 'winCondition', 'entryStyle', 'scoringCategories',
      // New roundConfig structure
      'roundConfig',
      // Legacy round fields (for backward compatibility)
      'scoringRoundTitles', 'roundsSequential', 'configurableRounds', 'roundBehavior', 'roundConfigText',
      // Phase end config
      'phaseEndConfig',
      // Tie-breaker config
      'tieBreakerConfig',
      'expansionOrder', 'variantBehavior'
    ];

    const unknown = {};
    Object.keys(parsed).forEach(key => {
      if (!knownFieldNames.includes(key)) {
        unknown[key] = parsed[key];
      }
    });

    return { gameData: parsed, unknownFields: unknown };
  }, [game]);

  // Valid status transitions
  // Note: wip with releaseChannel=null is new game, with channel set is modification
  const getValidTransitions = (currentStatus) => {
    const transitions = {
      [GAME_STATUS.WIP]: [
        { status: GAME_STATUS.PENDING_REVIEW, label: 'Submit for Review' },
        { status: GAME_STATUS.FLAGGED_DELETE, label: 'Flag for Deletion', color: 'error' }
      ],
      [GAME_STATUS.PENDING_REVIEW]: [
        { status: GAME_STATUS.READY_FOR_PROD, label: 'Approve for Beta', targetChannel: 'beta' },
        { status: GAME_STATUS.READY_FOR_PROD, label: 'Approve for Curated', targetChannel: 'curated', color: 'success' },
        { status: GAME_STATUS.WIP, label: 'Return to WIP' },
        { status: GAME_STATUS.FLAGGED_DELETE, label: 'Reject (Delete)', color: 'error' }
      ],
      [GAME_STATUS.READY_FOR_PROD]: [
        { status: GAME_STATUS.WIP, label: 'Return to WIP' }
      ],
      [GAME_STATUS.IN_PRODUCTION]: [
        { status: GAME_STATUS.WIP, label: 'Request Modifications' },
        { status: GAME_STATUS.FLAGGED_DELETE, label: 'Flag for Deletion', color: 'error' }
      ],
      [GAME_STATUS.FLAGGED_DELETE]: [
        { status: GAME_STATUS.WIP, label: 'Rescue (Return to WIP)' }
      ]
    };
    return transitions[currentStatus] || [];
  };

  const handleTransition = async (newStatus, targetChannel = null) => {
    setTransitionError('');
    try {
      await transitionMutation.mutateAsync({ id: parseInt(id), newStatus, targetChannel });
      // Brief pause then refresh to show updated status
      setTimeout(() => refetch(), 500);
    } catch (err) {
      setTransitionError(err.response?.data?.message || 'Status transition failed');
    }
  };

  const handleRevertToProduction = async () => {
    setTransitionError('');
    try {
      await cancelModificationsMutation.mutateAsync(parseInt(id));
      // Brief pause then refresh to show restored state
      setTimeout(() => refetch(), 500);
    } catch (err) {
      setTransitionError(err.response?.data?.message || 'Failed to revert to production');
    }
  };

  const handleCopyJson = async () => {
    if (!game) return;

    // Build complete game JSON as would be provided to the app
    const parsedGameData = typeof game.gameData === 'string'
      ? JSON.parse(game.gameData)
      : game.gameData;

    const completeJson = {
      gameCode: game.gameCode,
      gameName: game.gameName,
      language: game.language,
      schemaVersion: game.schemaVersion,
      category: game.category,
      isExpansion: game.isExpansion,
      isVariant: game.isVariant,
      parentGameCode: game.parentGameCode,
      originalGameCode: game.originalGameCode,
      contributorHandle: game.contributorHandle,
      releaseChannel: game.releaseChannel,
      createdAt: game.createdAt,
      modifiedAt: game.modifiedAt,
      ...parsedGameData
    };

    try {
      await navigator.clipboard.writeText(JSON.stringify(completeJson, null, 2));
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (isLoading) return <Loading message="Loading game details..." />;
  if (error) return <ErrorMessage error={error} onRetry={refetch} />;
  if (!game) return <Alert severity="error">Game not found</Alert>;

  const validTransitions = getValidTransitions(game.status);
  const canEdit = game.status === 'wip';
  // Can revert if: status is 'wip' AND has a releaseChannel (meaning it's a modification of production game)
  const canRevertToProduction = game.status === 'wip' && game.releaseChannel;

  // Format scoring type for display
  const getScoringTypeLabel = (type) => {
    // Use constants, with fallback for legacy types
    if (SCORING_TYPE_LABELS[type]) {
      return SCORING_TYPE_LABELS[type];
    }
    // Legacy support
    const legacyLabels = {
      'simple': 'Score',
      'multi': 'Multi-Round',
      'multi+': 'Multi-Round + Ending',
      'simpleRounds': 'Multi-Round',
      'multiScore': 'Multi-Round',
      'multiScorePlus': 'Multi-Round + Ending',
      'stream-events': 'Stream Events'  // Alternative format
    };
    return legacyLabels[type] || type;
  };

  // Check if the game has event-based categories
  const hasEventCategories = gameData.scoringCategories?.some(
    cat => cat.contexts?.includes('event')
  ) || false;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/games')}>
            <BackIcon />
          </IconButton>
          <Typography variant="h4">{game.gameName}</Typography>
          <StatusBadge status={game.status} />
          {game.releaseChannel && <ReleaseChannelBadge channel={game.releaseChannel} />}
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Tooltip title={copySuccess ? "Copied!" : "Copy JSON to clipboard"}>
            <IconButton onClick={handleCopyJson} color={copySuccess ? "success" : "default"}>
              <CopyIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh from server">
            <IconButton onClick={() => refetch()}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          {canEdit && (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/games/${id}/edit`)}
            >
              Edit
            </Button>
          )}
        </Box>
      </Box>

      {/* Transition Error */}
      {transitionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setTransitionError('')}>
          {transitionError}
        </Alert>
      )}

      {/* Status Transitions */}
      {validTransitions.length > 0 && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'primary.50' }}>
          <Typography variant="h6" gutterBottom>
            Workflow Actions
          </Typography>

          {/* Warning for expansions with parent status issues */}
          {isExpansion && parentPromotionStatus.reason && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {parentPromotionStatus.reason}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {validTransitions.map((transition) => {
              // Check if this action should be blocked for expansions based on parent status
              const isPromotionAction = transition.status === GAME_STATUS.READY_FOR_PROD;
              const isSubmitForReviewAction = transition.status === GAME_STATUS.PENDING_REVIEW;

              let shouldBlock = false;
              let blockReason = '';

              if (isExpansion) {
                if (isPromotionAction && !parentPromotionStatus.canPromote) {
                  shouldBlock = true;
                  blockReason = parentPromotionStatus.reason;
                } else if (isSubmitForReviewAction && !parentPromotionStatus.canSubmitForReview) {
                  shouldBlock = true;
                  blockReason = parentPromotionStatus.reason;
                }
              }

              return (
                <Tooltip
                  key={transition.label}
                  title={shouldBlock ? blockReason : ''}
                  arrow
                >
                  <span> {/* Wrapper needed for disabled button tooltip */}
                    <Button
                      variant="contained"
                      color={transition.color || 'primary'}
                      startIcon={<CheckIcon />}
                      onClick={() => handleTransition(transition.status, transition.targetChannel)}
                      disabled={transitionMutation.isLoading || shouldBlock}
                    >
                      {transition.label}
                    </Button>
                  </span>
                </Tooltip>
              );
            })}

            {/* Revert to Production button - only for WIP games with modifications */}
            {canRevertToProduction && (
              <Tooltip title="Discard changes and restore production version" arrow>
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<UndoIcon />}
                  onClick={handleRevertToProduction}
                  disabled={cancelModificationsMutation.isLoading}
                >
                  Revert to Production
                </Button>
              </Tooltip>
            )}
          </Box>
        </Paper>
      )}

      {/* Game Configuration - Wide Screen Layout */}
      <Grid container spacing={3}>
        {/* Column 1: Basic Information & Metadata */}
        <Grid item xs={12} lg={4}>
          {/* Basic Information */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Basic Information</Typography>
              <Divider sx={{ mb: 2 }} />
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>Game Code</TableCell>
                    <TableCell>{game.gameCode}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                    <TableCell>{game.gameName}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Category</TableCell>
                    <TableCell>{game.category || '—'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Language</TableCell>
                    <TableCell>{game.language}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Schema Version</TableCell>
                    <TableCell>{game.schemaVersion}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Game Version</TableCell>
                    <TableCell>{gameData.version || '—'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Contributor</TableCell>
                    <TableCell>{game.contributorHandle || '—'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Release Channel</TableCell>
                    <TableCell>
                      {game.releaseChannel ? <ReleaseChannelBadge channel={game.releaseChannel} /> : '—'}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Beta Feedback - only for beta channel games */}
          {isBetaChannel && feedbackData && (
            <Card sx={{ mb: 3, bgcolor: 'info.50' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Beta Feedback</Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <BetaFeedbackBadge
                    positiveCount={feedbackData.summary?.tested_ok || 0}
                    negativeCount={feedbackData.summary?.has_issues || 0}
                  />
                  <Typography variant="body2" color="text.secondary">
                    ({feedbackData.summary?.total_tests || 0} total tests)
                  </Typography>
                </Box>
                {isReadyForPromotion(feedbackData.summary?.tested_ok, feedbackData.summary?.has_issues) && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    This game is ready for promotion (20+ positive, 0 issues)
                  </Alert>
                )}
                {hasIssues(feedbackData.summary?.has_issues) && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    This game has reported issues that need attention
                  </Alert>
                )}
                {feedbackData.feedback?.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Recent Feedback:</Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Type</TableCell>
                          <TableCell>Categories</TableCell>
                          <TableCell>Comments</TableCell>
                          <TableCell>Date</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {feedbackData.feedback.slice(0, 5).map((fb, idx) => (
                          <TableRow key={idx}>
                            <TableCell>
                              <Chip
                                label={fb.feedback_type === 'tested_ok' ? 'OK' : 'Issue'}
                                color={fb.feedback_type === 'tested_ok' ? 'success' : 'error'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              {fb.issue_categories?.join(', ') || '—'}
                            </TableCell>
                            <TableCell>{fb.comments || '—'}</TableCell>
                            <TableCell>
                              {fb.tested_at ? format(new Date(fb.tested_at), 'MMM d, yyyy') : '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {feedbackData.feedback.length > 5 && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Showing 5 of {feedbackData.feedback.length} feedback entries
                      </Typography>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {/* Metadata */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Metadata</Typography>
              <Divider sx={{ mb: 2 }} />
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>Created</TableCell>
                    <TableCell>{game.createdAt ? format(new Date(game.createdAt), 'PPpp') : '—'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Modified</TableCell>
                    <TableCell>{game.modifiedAt ? format(new Date(game.modifiedAt), 'PPpp') : '—'}</TableCell>
                  </TableRow>
                  {game.syncNotes && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Sync Notes</TableCell>
                      <TableCell>{game.syncNotes}</TableCell>
                    </TableRow>
                  )}
                  {game.versionReleaseNotes && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Release Notes</TableCell>
                      <TableCell>{game.versionReleaseNotes}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>

        {/* Column 2: Player Configuration & Game Type */}
        <Grid item xs={12} lg={4}>
          {/* Player Configuration */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Player Configuration</Typography>
              <Divider sx={{ mb: 2 }} />
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>Min Players</TableCell>
                    <TableCell>{gameData.minPlayers ?? '—'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Max Players</TableCell>
                    <TableCell>{gameData.maxPlayers ?? '—'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Max Teams</TableCell>
                    <TableCell>{gameData.maxTeams ?? 0}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Fungible Teams</TableCell>
                    <TableCell>{gameData.fungibleTeams ? 'Yes' : 'No'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Team Pool Points</TableCell>
                    <TableCell>{gameData.teamPoolPoints ? 'Yes' : 'No'}</TableCell>
                  </TableRow>
                  {gameData.predefinedTeams && gameData.predefinedTeams.length > 0 && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Predefined Teams</TableCell>
                      <TableCell>{gameData.predefinedTeams.join(', ')}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Game Type & Relations */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Game Type & Relations</Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                <Chip label={game.isExpansion ? 'Expansion' : 'Base Game'}
                      color={game.isExpansion ? 'secondary' : 'default'} size="small" />
                <Chip label={game.isVariant ? 'Variant' : 'Standard'}
                      color={game.isVariant ? 'secondary' : 'default'} size="small" />
              </Box>
              <Table size="small">
                <TableBody>
                  {game.parentGameCode && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>Parent Game</TableCell>
                      <TableCell>{game.parentGameCode}</TableCell>
                    </TableRow>
                  )}
                  {game.originalGameCode && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Original Game</TableCell>
                      <TableCell>{game.originalGameCode}</TableCell>
                    </TableRow>
                  )}
                  {gameData.expansionOrder !== undefined && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Expansion Order</TableCell>
                      <TableCell>{gameData.expansionOrder}</TableCell>
                    </TableRow>
                  )}
                  {gameData.variantBehavior && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Variant Behavior</TableCell>
                      <TableCell>{gameData.variantBehavior}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>

        {/* Column 3: Scoring Configuration */}
        <Grid item xs={12} lg={4}>
          {/* Scoring Configuration */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Scoring Configuration</Typography>
              <Divider sx={{ mb: 2 }} />
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>Scoring Type</TableCell>
                    <TableCell>
                      <Box>
                        {gameData.scoringType ? getScoringTypeLabel(gameData.scoringType) : '—'}
                        {gameData.scoringType === SCORING_TYPES.STREAM_EVENTS && (
                          <Chip label="Events" size="small" color="info" sx={{ ml: 1 }} />
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Win Condition</TableCell>
                    <TableCell>{gameData.winCondition || '—'}</TableCell>
                  </TableRow>
                  {gameData.scoringType !== 'winLoss' && gameData.scoringType !== SCORING_TYPES.STREAM_EVENTS && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Entry Style</TableCell>
                      <TableCell>
                        {gameData.entryStyle === 'compact' ? 'Compact (single value per round)' :
                         gameData.entryStyle === 'popup' ? 'Popup (tap to open entry form)' :
                         'Inline (grid layout)'}
                      </TableCell>
                    </TableRow>
                  )}
                  {hasEventCategories && gameData.scoringType !== SCORING_TYPES.STREAM_EVENTS && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Hybrid Events</TableCell>
                      <TableCell>
                        <Chip label="Yes" size="small" color="info" />
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          Some categories support ad-hoc events
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              {gameData.scoringType === SCORING_TYPES.STREAM_EVENTS && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Stream Events: Scores come from free-flowing events rather than structured rounds.
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Round Configuration */}
          {(gameData.roundConfig || gameData.scoringRoundTitles || gameData.roundsSequential !== undefined || gameData.roundConfigText) && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Round Configuration</Typography>
                <Divider sx={{ mb: 2 }} />
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>Sequential</TableCell>
                      <TableCell>
                        {gameData.roundConfig
                          ? (gameData.roundConfig.sequential !== false ? 'Yes' : 'No')
                          : (gameData.roundsSequential !== false ? 'Yes' : 'No')}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Expanding</TableCell>
                      <TableCell>
                        {gameData.roundConfig
                          ? (gameData.roundConfig.expanding ? 'Yes' : 'No')
                          : (gameData.configurableRounds ? 'Yes' : 'No')}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Behavior</TableCell>
                      <TableCell>
                        {gameData.roundConfig?.behavior
                          ? (ROUND_BEHAVIOR_LABELS[gameData.roundConfig.behavior] || gameData.roundConfig.behavior)
                          : (gameData.roundBehavior || '—')}
                      </TableCell>
                    </TableRow>
                    {(gameData.roundConfig?.titles?.length > 0 || gameData.scoringRoundTitles?.length > 0) && (
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Round Titles</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(gameData.roundConfig?.titles || gameData.scoringRoundTitles || []).map((title, idx) => (
                              <Chip key={idx} label={title} size="small" />
                            ))}
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                    {(gameData.roundConfig?.configText || gameData.roundConfigText) && (
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Config Text</TableCell>
                        <TableCell sx={{ whiteSpace: 'pre-wrap' }}>
                          {gameData.roundConfig?.configText || gameData.roundConfigText}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Phase End Configuration */}
          {gameData.phaseEndConfig && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Phase End Configuration</Typography>
                <Divider sx={{ mb: 2 }} />
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>Type</TableCell>
                      <TableCell>
                        {PHASE_END_TYPE_LABELS[gameData.phaseEndConfig.type] || gameData.phaseEndConfig.type}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Trigger</TableCell>
                      <TableCell>
                        {PHASE_TRIGGER_LABELS[gameData.phaseEndConfig.trigger] || gameData.phaseEndConfig.trigger}
                      </TableCell>
                    </TableRow>
                    {gameData.phaseEndConfig.triggerAfterRounds && (
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Trigger After Rounds</TableCell>
                        <TableCell>{gameData.phaseEndConfig.triggerAfterRounds}</TableCell>
                      </TableRow>
                    )}
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Expanding</TableCell>
                      <TableCell>{gameData.phaseEndConfig.expanding ? 'Yes' : 'No'}</TableCell>
                    </TableRow>
                    {gameData.phaseEndConfig.titles?.length > 0 && (
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Phase Titles</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {gameData.phaseEndConfig.titles.map((title, idx) => (
                              <Chip key={idx} label={title} size="small" />
                            ))}
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Setup Text */}
          {gameData.setupText && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Setup Instructions</Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {gameData.setupText}
                </Typography>
              </CardContent>
            </Card>
          )}

        </Grid>
      </Grid>

      {/* Scoring Categories */}
      {gameData.scoringCategories && gameData.scoringCategories.length > 0 && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Scoring Categories</Typography>
            <Divider sx={{ mb: 2 }} />
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Title</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Formula</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Contexts</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Matrix</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Action</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Selectable</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Depends On</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {gameData.scoringCategories.map((cat, idx) => {
                  // Get contexts - migrate from legacy booleans if needed
                  let contexts = cat.contexts;
                  if (!contexts) {
                    contexts = [];
                    if (cat.showInRounds !== false) contexts.push('round');
                    if (cat.showInPhaseEnd === true) contexts.push('phaseEnd');
                    if (contexts.length === 0) contexts.push('round');
                  }
                  const contextLabels = {
                    round: 'Round',
                    phaseEnd: 'Phase',
                    gameEnd: 'End',
                    tieBreaker: 'Tie',
                    event: 'Event'
                  };
                  const hasMatrixLabels = cat.rowLabel && cat.columnLabel;
                  return (
                    <TableRow key={idx}>
                      <TableCell>{cat.id}</TableCell>
                      <TableCell>{cat.title || '—'}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{cat.formula || '—'}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {contexts.map(ctx => (
                            <Chip key={ctx} label={contextLabels[ctx] || ctx} size="small" variant="outlined" />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {hasMatrixLabels ? (
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                            <Chip label="M" size="small" color="secondary" sx={{ fontWeight: 'bold', minWidth: 24 }} />
                            <Box sx={{ fontSize: '0.75rem' }}>
                              <Box>{cat.rowLabel}</Box>
                              <Box sx={{ color: 'text.secondary' }}>{cat.columnLabel}</Box>
                            </Box>
                          </Box>
                        ) : '—'}
                      </TableCell>
                      <TableCell>{cat.action || '—'}</TableCell>
                      <TableCell>
                        {cat.selectable === true
                          ? 'Default Selected'
                          : cat.selectable === false
                          ? 'Default Deselected'
                          : cat.selectable === undefined
                          ? '—'
                          : 'Not Selectable'}
                      </TableCell>
                      <TableCell>{cat.dependsOn || '—'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Tie-Breaker Configuration - Full Width */}
      {gameData.tieBreakerConfig?.enabled && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Tie-Breaker Configuration</Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              {gameData.tieBreakerConfig.groups?.map((group, groupIdx) => (
                <Grid item xs={12} md={6} lg={4} key={group.id || groupIdx}>
                  <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, height: '100%' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {groupIdx + 1}. {group.title || '(Untitled)'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Win Condition: {group.winCondition === 'high' ? 'Highest wins' : 'Lowest wins'}
                    </Typography>
                    {group.questions?.length > 0 && (
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', py: 0.5 }}>Question</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', py: 0.5 }}>Formula</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {group.questions.map((q, qIdx) => (
                            <TableRow key={q.id || qIdx}>
                              <TableCell sx={{ py: 0.5 }}>{q.title || '—'}</TableCell>
                              <TableCell sx={{ py: 0.5, fontFamily: 'monospace' }}>{q.formula || '*1'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </Box>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Unknown/Extra JSON Fields (Leftovers) */}
      {Object.keys(unknownFields).length > 0 && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Additional Data (Unknown Fields)</Typography>
            <Divider sx={{ mb: 2 }} />
            <Paper sx={{ p: 2, bgcolor: 'grey.100', maxHeight: 300, overflow: 'auto' }}>
              <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: '0.875rem' }}>
                {JSON.stringify(unknownFields, null, 2)}
              </pre>
            </Paper>
          </CardContent>
        </Card>
      )}

    </Box>
  );
};
